import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BOOK, openAmazon } from "@/lib/businessMastery";
import { BusinessMasteryCover } from "@/components/BusinessMasteryCard";
import { Play, Pause, RotateCcw, ArrowRight, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MODULES } from "@/pages/Demo";
import api from "@/lib/api";

const mod = (id) => MODULES.find((m) => m.id === id);

// Anonymous demo-video funnel events (public page). Reuses existing analytics via /public/track.
const dvTrack = (event, props = {}) => api.post("/public/track", { event, source: "demo_video", ...props }).catch(() => {});

// Scene = { type, moduleId, dur(seconds), lines:[headline, sub?] }
// ~30s Fairview Systems Demo Video. Durations synced to the per-scene voiceover
// (backend/scripts/gen_demo_vo.py output). Total ≈ 31.7s.
const SCENES = [
  { type: "intro", dur: 3.92, lines: ["Your Business. One Operating System.", "Build It Right. Systemize It. Scale It."] },
  { type: "module", moduleId: "dashboard", dur: 5.29, lines: ["See your business at a glance."] },
  { type: "module", moduleId: "formation", dur: 2.43, lines: ["Build the right foundation."] },
  { type: "module", moduleId: "credit", dur: 2.10, lines: ["Understand your business credit."] },
  { type: "module", moduleId: "funding", dur: 1.47, lines: ["Prepare for funding."] },
  { type: "module", moduleId: "ai", dur: 1.95, lines: ["AI-powered guidance."] },
  { type: "module", moduleId: "education", dur: 5.46, bm: true, lines: ["Learn while you build."] },
  { type: "module", moduleId: "progress", dur: 3.44, lines: ["Start. Manage. Systemize. Scale."] },
  { type: "outro", dur: 5.67, lines: ["Start Your Free Trial"] },
];
const TOTAL = SCENES.reduce((s, x) => s + x.dur, 0);
const STARTS = SCENES.reduce((a, s, i) => { a.push(i ? a[i - 1] + SCENES[i - 1].dur : 0); return a; }, []);

export default function DemoVideo() {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [started, setStarted] = useState(false);
  const completedRef = useRef(false);

  const idx = Math.max(0, STARTS.findLastIndex((s) => t >= s));
  const scene = SCENES[idx];
  const ended = t >= TOTAL;

  const tick = useCallback(() => {
    const a = audioRef.current;
    setT((prev) => {
      const now = a && !a.paused ? a.currentTime : prev + 1 / 60;
      return now >= TOTAL ? TOTAL : now;
    });
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (playing) rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, tick]);

  useEffect(() => {
    if (ended) {
      setPlaying(false);
      audioRef.current?.pause();
      if (!completedRef.current) { completedRef.current = true; dvTrack("demo_video_complete"); }
    }
  }, [ended]);

  const start = () => {
    setStarted(true); setPlaying(true); setT(0);
    completedRef.current = false;
    dvTrack("demo_video_start");
    const a = audioRef.current;
    if (a) { a.currentTime = 0; a.play().catch(() => {}); }
  };
  const toggle = () => {
    const a = audioRef.current;
    if (playing) { a?.pause(); setPlaying(false); }
    else { if (ended) return start(); a?.play().catch(() => {}); setPlaying(true); }
  };
  const restart = () => { setT(0); completedRef.current = false; const a = audioRef.current; if (a) { a.currentTime = 0; if (playing) a.play().catch(() => {}); } };
  const startTrial = (ctaLocation) => { dvTrack("demo_video_cta_click", { cta_location: ctaLocation }); navigate("/register?plan=growth&source=demo_video"); };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white flex flex-col" data-testid="demo-video">
      <audio ref={audioRef} src="/assets/fairview-demo-vo.mp3" preload="auto" />
      <style>{`
        @keyframes fvFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        .fv-fade { animation: fvFade .6s ease both; }
        @keyframes fvKen { from { transform: scale(1.04); } to { transform: scale(1); } }
        .fv-kenburns { animation: fvKen 8s ease-out both; }
      `}</style>

      {/* Stage */}
      <div className="relative flex-1 overflow-hidden">
        {/* Brand frame chrome */}
        <div className="absolute top-0 inset-x-0 h-14 fv-glass border-b border-white/8 flex items-center justify-between px-5 z-20">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg fv-gold-gradient flex items-center justify-center"><span className="font-heading text-[#0A0A0B] text-lg font-bold">F</span></div>
            <div className="leading-tight"><div className="font-heading tracking-wide">Fairview</div><div className="text-[8px] tracking-[0.25em] uppercase text-[#D4AF37]">Systems</div></div>
          </div>
          <span className="text-[10px] px-2.5 py-1 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#D4AF37]">Demo — Sample Data Only</span>
        </div>

        {/* Scene backdrop */}
        <div className="absolute inset-0 pt-14 flex items-center justify-center">
          {!started ? (
            <div className="text-center px-6">
              <div className="w-20 h-20 rounded-2xl fv-gold-gradient mx-auto flex items-center justify-center mb-6"><span className="font-heading text-[#0A0A0B] text-4xl font-bold">F</span></div>
              <h1 className="font-heading text-4xl sm:text-5xl mb-3">Fairview Systems<span className="align-super text-lg">™</span></h1>
              <p className="text-[#A1A1AA] mb-8">Your Business Operating System · 30-second demo video</p>
              <Button onClick={start} data-testid="demo-video-play" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-12 px-8 text-base"><Play className="w-5 h-5 mr-2" /> Play Demo Video</Button>
            </div>
          ) : scene.type === "intro" ? (
            <SceneText lines={scene.lines} big />
          ) : scene.type === "outro" ? (
            <div className="text-center px-6 fv-fade" key="outro">
              <div className="w-16 h-16 rounded-2xl fv-gold-gradient mx-auto flex items-center justify-center mb-5"><Crown className="w-8 h-8 text-[#0A0A0B]" /></div>
              <h2 className="font-heading text-4xl mb-1">Fairview Systems<span className="align-super text-base">™</span></h2>
              <p className="text-[#D4AF37] tracking-wide mb-1">Your Business Operating System</p>
              <p className="text-[#A1A1AA] mb-6">Build It Right. Systemize It. Scale It.</p>
              <Button onClick={() => startTrial("outro")} data-testid="demo-video-cta" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-12 px-8 text-base">Start Your Free Trial <ArrowRight className="w-4 h-4 ml-2" /></Button>
              <p className="text-[#71717A] text-sm mt-4">www.fairviewsystems.net</p>
            </div>
          ) : (
            <div key={scene.moduleId} className="w-full h-full px-4 sm:px-10 pb-24 pt-4 overflow-hidden">
              <div className="max-w-5xl mx-auto fv-kenburns">
                <div className="text-[#D4AF37] text-[11px] tracking-[0.2em] uppercase mb-1">{mod(scene.moduleId)?.label}</div>
                <div className="pointer-events-none select-none">{mod(scene.moduleId)?.render()}</div>
              </div>
            </div>
          )}
        </div>

        {started && scene.bm && (
          <div data-testid="demo-bm-overlay" className="absolute z-20 fv-fade left-3 right-3 top-16 mx-auto max-w-xs w-auto lg:left-auto lg:right-10 lg:top-20 lg:mx-0 lg:w-64 lg:max-w-none rounded-xl border border-[#D4AF37]/40 bg-[#0F0F10]/95 p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Featured educational resource</div>
            <div className="flex gap-3">
              <BusinessMasteryCover className="w-14 h-20" compact />
              <div className="min-w-0">
                <div className="font-heading text-white text-base leading-tight">{BOOK.title}</div>
                <div className="text-xs text-[#A1A1AA]">By {BOOK.author}</div>
                <div className="text-[10px] text-[#71717A] mt-0.5">Available on Amazon</div>
              </div>
            </div>
            <button onClick={() => openAmazon({ source: "demo_video", location: "education_scene" }, { public: true })} data-testid="demo-bm-buy"
              className="fv-gold-gradient text-[#0A0A0B] text-sm font-semibold rounded-full px-4 h-9 mt-3 w-full">Buy Business Mastery</button>
          </div>
        )}
        {/* Caption overlay for module scenes */}
        {started && scene.type === "module" && (
          <div key={"cap" + idx} className="absolute bottom-24 inset-x-0 mx-auto z-20 w-[92%] max-w-3xl text-center fv-fade">
            <span className="inline-block max-w-full bg-black/55 backdrop-blur-md border border-[#D4AF37]/25 rounded-2xl px-4 sm:px-6 py-3 sm:py-4">
              <span className="font-heading text-lg sm:text-2xl lg:text-3xl">{scene.lines[0]}</span>
            </span>
          </div>
        )}
      </div>

      {/* Player controls */}
      <div className="fv-glass border-t border-[#D4AF37]/20 px-4 sm:px-6 py-3 z-30">
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-3">
          <div className="h-full fv-gold-gradient transition-[width] duration-200" style={{ width: `${Math.min(100, (t / TOTAL) * 100)}%` }} data-testid="demo-video-progress" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button onClick={toggle} disabled={!started} data-testid="demo-video-toggle" className="w-10 h-10 rounded-full fv-gold-gradient text-[#0A0A0B] flex items-center justify-center disabled:opacity-40" aria-label={playing ? "Pause" : "Play"}>{playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}</button>
            <button onClick={restart} disabled={!started} data-testid="demo-video-restart" className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-[#A1A1AA] disabled:opacity-40" aria-label="Restart"><RotateCcw className="w-4 h-4" /></button>
            <span className="text-xs text-[#71717A] tabular-nums">{String(Math.floor(t)).padStart(2, "0")}s / {Math.round(TOTAL)}s</span>
          </div>
          <Button onClick={() => startTrial("controls")} data-testid="demo-video-trial" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-5 h-9 text-sm">Start Free Trial</Button>
        </div>
      </div>
    </div>
  );
}

function SceneText({ lines, big }) {
  return (
    <div className="text-center px-6 fv-fade">
      <h1 className={`font-heading ${big ? "text-4xl sm:text-6xl" : "text-3xl sm:text-4xl"} tracking-tight mb-4`}>{lines[0]}</h1>
      {lines[1] && <p className="text-[#D4AF37] text-lg sm:text-2xl tracking-wide">{lines[1]}</p>}
    </div>
  );
}
